'use client';

import { useState } from 'react';
import { Inbox, Loader2, ExternalLink } from 'lucide-react';
import {
  useAdminInboxTickets,
  useFilteredInboxTickets,
  type InboxFilter,
} from '../hooks/use-admin-support-inbox';
import { AdminInboxRow } from './admin-inbox-row';

interface AdminInboxViewProps {
  onSelectTicket: (id: string) => void;
}

const FILTERS: { value: InboxFilter; label: string }[] = [
  { value: 'awaiting_reply', label: 'Așteaptă răspuns' },
  { value: 'awaiting_user', label: 'Așteaptă user' },
  { value: 'all_active', label: 'Toate active' },
  { value: 'resolved', label: 'Rezolvate' },
];

const EMPTY_MESSAGES: Record<InboxFilter, string> = {
  awaiting_reply: 'Niciun tichet nu așteaptă răspuns. Bravo!',
  awaiting_user: 'Niciun tichet în așteptare după client.',
  all_active: 'Niciun tichet activ.',
  resolved: 'Niciun tichet rezolvat sau închis.',
};

export function AdminInboxView({ onSelectTicket }: AdminInboxViewProps) {
  const [filter, setFilter] = useState<InboxFilter>('awaiting_reply');
  const { isLoading } = useAdminInboxTickets();
  const tickets = useFilteredInboxTickets(filter);

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-neutral-200/70 bg-white/40 px-3 py-2 backdrop-blur-sm dark:border-neutral-800/70 dark:bg-neutral-900/40">
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              className={`rounded-md px-2 py-1 text-[0.6875rem] font-medium transition-colors ${
                filter === f.value
                  ? 'bg-amber-500 text-white shadow-sm'
                  : 'bg-transparent text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-10 text-neutral-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : tickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
            <Inbox
              className="h-8 w-8 text-neutral-300 dark:text-neutral-700"
              strokeWidth={1.5}
            />
            <p className="text-[0.75rem] text-neutral-500 dark:text-neutral-500">
              {EMPTY_MESSAGES[filter]}
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {tickets.map((ticket) => (
              <AdminInboxRow
                key={ticket.id}
                ticket={ticket}
                onSelect={onSelectTicket}
              />
            ))}
          </div>
        )}
      </div>

      <footer className="border-t border-neutral-200/70 bg-white/40 px-3 py-2 backdrop-blur-sm dark:border-neutral-800/70 dark:bg-neutral-900/40">
        <a
          href="/admin/support"
          className="inline-flex items-center gap-1 text-[0.6875rem] font-medium text-neutral-500 hover:text-amber-700 dark:text-neutral-500 dark:hover:text-amber-400"
        >
          Vezi tot în pagina admin
          <ExternalLink className="h-2.5 w-2.5" strokeWidth={2} />
        </a>
      </footer>
    </div>
  );
}
